// api/runtime-config.ts
//
// Deploy-time configuration for a build-time bundler.
//
// Create React App inlines `process.env.REACT_APP_*` into the bundle when
// webpack runs, and the production image then serves those already-compiled
// files with `serve -s build`. So a container env var set at deploy time cannot
// reach the app — a fact that was silently untrue for a long time: both
// compose.dev.yml and the CI workflow looked like they configured the API URL
// and neither did.
//
// The fix is a value the BROWSER resolves on page load:
//
//   1. window.__ENV__      — written by docker-entrypoint.sh at container start
//   2. REACT_APP_USE_LOCAL_API === 'true' — unchanged local-dev switch
//   3. process.env.REACT_APP_* — build-time, still used by `npm start`
//   4. derived from window.location — the deployment's hostname convention
//   5. a literal — last resort
//
// Step 4 is what lets ONE image serve kivo.skin and dev.kivo.skin correctly.
// The Helm chart derives every hostname from a single `global.domain` key
// (`<domain>`, `api.<domain>`, `admin.<domain>`), so the API host is a pure
// function of the host in the address bar. At cutover `global.domain` flips
// from dev.kivo.skin to kivo.skin and this follows with no rebuild.
//
// Step 1 remains the escape hatch for when the API is NOT at `api.<host>`.

declare global {
  interface Window {
    __ENV__?: Record<string, string | undefined>;
  }
}

/** Last-resort API base, used only when nothing else resolves. */
export const FALLBACK_API_BASE_URL = 'https://api.kivo.skin/v1/';

/** Default local API base, kept identical to the previous hardcoded value. */
export const LOCAL_API_BASE_URL = 'http://api.skindetector.localhost/v1/';

/**
 * Read a deploy-time value injected by `/config/env.js`.
 * Empty strings are treated as absent so an unset var in the entrypoint's
 * template never shadows a lower-precedence source.
 */
export function runtimeValue(key: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const value = window.__ENV__?.[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Public base URL for a mirror of the on-device model's weights, or undefined
 * when the deployment has none.
 *
 * Deliberately has no credential counterpart. This value is read from
 * `/config/env.js`, which every visitor downloads, so anything secret placed
 * beside it would be secret from nobody. A private mirror needs a presigned URL
 * minted by the API, not a key in the bundle.
 */
export function modelMirrorBaseUrl(): string | undefined {
  return runtimeValue('MODEL_MIRROR_BASE_URL')
    ?? process.env.REACT_APP_MODEL_MIRROR_BASE_URL
    ?? undefined;
}

/**
 * Normalise an API base so callers can always do `${API_BASE_URL}${path}`.
 *
 * Two independent problems this solves:
 *  - `apiFetch` strips the leading slash off its path, so the base MUST end
 *    with one.
 *  - the two repos disagree on whether the configured value carries the API
 *    version: the frontend's compose passes `${API_BASE_URL}/v1/` while the
 *    admin's passes a bare `${API_BASE_URL}`. A base with no path at all gets
 *    `v1/` appended so both spellings land in the same place.
 */
export function normalizeApiBase(raw: string): string {
  const trimmed = raw.trim().replace(/\/+$/, '');
  if (trimmed === '') return FALLBACK_API_BASE_URL;
  try {
    const url = new URL(trimmed);
    // Origin only (no path) — the version segment was not included.
    if (url.pathname === '/' || url.pathname === '') {
      return `${url.origin}/v1/`;
    }
  } catch {
    // Not absolute (e.g. a relative base). Fall through to the plain form.
  }
  return `${trimmed}/`;
}

/**
 * Is this a value we are willing to treat as a configured API base?
 *
 * Guards a live footgun rather than a hypothetical one: compose.dev.yml builds
 * the variable by string concatenation (`${API_BASE_URL}/v1/`), so an unset or
 * empty API_BASE_URL yields the RELATIVE string "/v1/". Accepting that would
 * silently point every request at the page's own origin — the SPA host, which
 * serves index.html for any path — instead of at the API. Falling through to
 * host derivation is both safer and almost always right.
 */
export function isUsableApiBase(value: string | undefined): value is string {
  if (!value) return false;
  try {
    const { protocol } = new URL(value.trim());
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Derive the API base from the host currently in the address bar.
 *
 *   kivo.skin              -> https://api.kivo.skin/v1/
 *   dev.kivo.skin          -> https://api.dev.kivo.skin/v1/
 *   admin.kivo.skin        -> https://api.kivo.skin/v1/
 *   admin.dev.kivo.skin    -> https://api.dev.kivo.skin/v1/
 *   skindetector.localhost -> http://api.skindetector.localhost/v1/
 *
 * A leading `admin.` label is dropped so the admin SPA reaches the same API as
 * the web app. `.localhost` keeps http because local dev has no certificates.
 */
export function deriveApiBase(host: string, protocol: string): string | undefined {
  if (!host) return undefined;

  // Bare hostnames and IPs (jsdom's "localhost", "127.0.0.1:3000") have no
  // domain to hang an `api.` label off — deriving there would invent a host
  // that does not exist, so leave it to the caller's next fallback.
  const [hostname] = host.split(':');
  if (!hostname.includes('.') || /^[\d.]+$/.test(hostname)) return undefined;

  const apiHost = `api.${host.replace(/^admin\./, '')}`;
  const scheme = /\.localhost(:|$)/.test(host) ? 'http:' : protocol || 'https:';
  return `${scheme}//${apiHost}/v1/`;
}

/** Resolve the API base URL through the full precedence chain. */
export function resolveApiBaseUrl(): string {
  const override = runtimeValue('API_BASE_URL');
  if (isUsableApiBase(override)) return normalizeApiBase(override);

  if (process.env.REACT_APP_USE_LOCAL_API === 'true') {
    return normalizeApiBase(
      process.env.REACT_APP_LOCAL_API_BASE_URL ?? LOCAL_API_BASE_URL,
    );
  }

  const buildTime = process.env.REACT_APP_API_BASE_URL;
  if (isUsableApiBase(buildTime)) return normalizeApiBase(buildTime);

  if (typeof window !== 'undefined') {
    const derived = deriveApiBase(window.location.host, window.location.protocol);
    if (derived) return derived;
  }

  return FALLBACK_API_BASE_URL;
}

/**
 * Resolve the public web origin (the user-facing SPA), as opposed to the API.
 * Used for links that must point at the web app from the admin panel.
 * `admin.dev.kivo.skin` -> `https://dev.kivo.skin`.
 */
export function resolveWebBaseUrl(): string {
  const override = runtimeValue('WEB_BASE_URL');
  if (override) return override.replace(/\/+$/, '');

  if (typeof window !== 'undefined' && window.location.host) {
    const { host, protocol } = window.location;
    return `${protocol}//${host.replace(/^admin\./, '')}`;
  }

  return 'https://kivo.skin';
}
